import { LiveAnnouncer } from "@angular/cdk/a11y";
import { DialogRef } from "@angular/cdk/dialog";
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    HostListener,
    Inject,
    OnDestroy,
    OnInit,
} from "@angular/core";
import { UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { TranslateService } from "@ngx-translate/core";

import {
    distinctUntilChanged,
    filter,
    map,
    Observable,
    Subscription,
    switchMap,
    take,
} from "rxjs";

import { LayoutFacade } from "@hermes/aphrodite/layout";
import {
    Context,
    COUNTRY,
    Settings,
    StorageService,
    WINDOW,
} from "@hermes/app-core";
import { DEFAULT_ADD_TO_CART_ERROR } from "@hermes/fragments/call-to-action-add-to-cart";
import { ConfigMessageError } from "@hermes/fragments/product-utils";
import { Country } from "@hermes/locale";
import { AddToCartFacade } from "@hermes/states/add-to-cart";
import { ModalFacade } from "@hermes/states/modal";
import { ProductPageFacade } from "@hermes/states/product-page";
import {
    getFirstSkuProductFromCategories,
    SINGLE_PRODUCT_PAGE,
    VtoBeautyFacade,
    VtoModalConfiguration,
    VtoModalStatus,
    VtoPageType,
} from "@hermes/states/vto-beauty";
import { AnalyticsService } from "@hermes/utils/analytics";

import { ESCAPE } from "@hermes/utils-generic/constants";
import { generateLink } from "@hermes/utils-generic/helpers";

import { CameraButtonClickAnalyticsEvent } from "../../events/camera-button-click.event";
import {
    getApprovalText,
    getScene7ImageRatio,
    getScene7Resolution,
} from "../../helpers/ymk-sdk.helper";
import { YmkSdkService } from "../../services/ymk-sdk.service";

const PRIVACY_LINK_KEY = "hermes_footer.legal.privacy.link.relative";
const PRIVACY_LINK_TEXT = $localize`:@@hermes.product-page.vto-approval-text-link:Privacy`;
const SCENE7_VTO_APPROVAL_SLUG = "vto_approval";

@Component({
    selector: "h-virtual-try-on-modal",
    templateUrl: "./virtual-try-on-modal.component.html",
    styleUrls: ["./virtual-try-on-modal.component.scss"],
    providers: [YmkSdkService],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VirtualTryOnModalComponent implements OnInit, OnDestroy {
    public imgSrc = this.buildImageUrl();
    public approvalText: SafeHtml = "";
    public approvalFormControl = new UntypedFormControl("");
    public addToCartError: ConfigMessageError = DEFAULT_ADD_TO_CART_ERROR;
    public approvalFormGroup = new UntypedFormGroup({
        checkApproval: this.approvalFormControl,
    });
    public vtoModalConfiguration: VtoModalConfiguration | undefined;
    private subscription = new Subscription();
    private temporarilyDisableClose: boolean = false;

    private pageType: VtoPageType = SINGLE_PRODUCT_PAGE;

    constructor(
        public layoutFacade: LayoutFacade,
        public vtoBeautyFacade: VtoBeautyFacade,
        private liveAnnouncer: LiveAnnouncer,
        private analyticsService: AnalyticsService,
        private domSanitizer: DomSanitizer,
        private translateService: TranslateService,
        private storageService: StorageService,
        private settings: Settings,
        private productPageFacade: ProductPageFacade,
        public ymkSdkService: YmkSdkService,
        private context: Context,
        @Inject(COUNTRY) private country: Country,
        private addToCartFacade: AddToCartFacade,
        private dialogReference: DialogRef<VirtualTryOnModalComponent>,
        public changeDetector: ChangeDetectorRef,
        @Inject(WINDOW) private window: Window,
        private modalFacade: ModalFacade,
    ) {
        this.subscription.add(
            this.vtoBeautyFacade.modalStatus$
                .pipe(
                    distinctUntilChanged(),
                    filter(() => this.context.isInBrowserMode()),
                )
                .subscribe((modalStatus) => {
                    const actions: Partial<
                        Record<VtoModalStatus, () => unknown>
                    > = {
                        ["MODAL_CLOSED"]: () => {
                            this.dialogReference.close();
                        },
                        ["CAMERA_LOADING"]: () =>
                            this.ymkSdkService.launchCamera(),
                        ["CAMERA_LOADED"]: () =>
                            this.ymkSdkService.applyMakeupConfiguration(),
                    };
                    actions[modalStatus]?.();
                }),
        );
        if (this.isApprovalInSession()) {
            this.vtoBeautyFacade.userApprove();
        } else {
            this.vtoBeautyFacade.userDisapprove();
        }
    }

    @HostListener("window:resize")
    public onResize(): void {
        this.imgSrc = this.buildImageUrl();
        this.changeDetector.markForCheck();
    }

    public ngOnInit(): void {
        this.subscription.add(
            this.vtoBeautyFacade.pageType$
                .pipe(
                    switchMap((pageType) => {
                        this.pageType = pageType as VtoPageType;
                        return this.getSkuObservableFromPageType(this.pageType);
                    }),
                    filter(Boolean),
                )
                .subscribe((currentVtoSku) => {
                    this.updateCurrentSku(currentVtoSku);
                }),
        );

        // We open dialog with disableClose parameter to handle Escape here
        this.subscription.add(
            this.dialogReference.keydownEvents
                .pipe(
                    filter((keyboardEvent) => keyboardEvent.key === ESCAPE),
                    filter(() => !this.temporarilyDisableClose),
                )
                .subscribe(() => this.vtoBeautyFacade.closeModal()),
        );

        // Case user do ATC and go to cart -> must close vto modal
        this.subscription.add(
            this.modalFacade.modalClosed$
                .pipe(
                    filter((success) => success),
                    take(1),
                )
                .subscribe(() => this.vtoBeautyFacade.closeModal()),
        );

        this.subscription.add(
            this.translateService
                .get(PRIVACY_LINK_KEY)
                .subscribe((privacyUrl) => {
                    const privacyLink = generateLink(
                        privacyUrl,
                        PRIVACY_LINK_TEXT,
                    );
                    this.approvalText =
                        this.domSanitizer.bypassSecurityTrustHtml(
                            getApprovalText(
                                this.country.countryCode,
                                privacyLink,
                            ),
                        );
                    this.changeDetector.markForCheck();
                }),
        );

        this.subscription.add(
            this.vtoBeautyFacade.isApproval$.subscribe((isApproval) => {
                if (isApproval) {
                    this.vtoBeautyFacade.launchCamera();
                } else {
                    this.liveAnnouncer.announce(
                        $localize`:@@hermes.product-page.vto-approval-title:Approval to use the service`,
                    );
                }
            }),
        );

        this.subscription.add(
            this.addToCartFacade.addToCartBasket$.subscribe(
                (addToCartBasket) => {
                    this.temporarilyDisableClose =
                        !!addToCartBasket.notifDisplayed;
                    this.changeDetector.markForCheck();
                },
            ),
        );
    }

    public ngOnDestroy(): void {
        this.ymkSdkService.closeVto();
        this.subscription.unsubscribe();
        this.vtoBeautyFacade.destroyModal();
    }

    public userApprove(): void {
        if (this.approvalFormControl.value) {
            // can be moved to userApprove effect
            this.analyticsService.sendData(
                new CameraButtonClickAnalyticsEvent({
                    pagetype: this.pageType,
                }),
            );
            this.vtoBeautyFacade.userApprove();
            this.setApprovalInSession(true);
        }
    }

    public closeModal(event?: MouseEvent): void {
        event?.stopPropagation();
        this.vtoBeautyFacade.closeModal();
    }

    public setApprovalInSession(approval: boolean): void {
        this.storageService.setSessionStorageItem("vtoApproval", approval);
    }

    public isApprovalInSession(): boolean {
        return (
            this.storageService.getSessionStorageItem("vtoApproval") || false
        );
    }

    private buildImageUrl(): string {
        return `url('${
            this.settings.assetsUrlProduct
        }${SCENE7_VTO_APPROVAL_SLUG}_${
            this.country.countryCode
        }_${getScene7ImageRatio(
            this.window.innerWidth,
            this.window.innerHeight,
        )}?defaultimage=${SCENE7_VTO_APPROVAL_SLUG}_default_${getScene7ImageRatio(
            this.window.innerWidth,
            this.window.innerHeight,
        )}&wid=${getScene7Resolution(this.window.innerWidth)}')`;
    }

    private updateCurrentSku(currentVtoSku: string) {
        this.vtoBeautyFacade.updateSelectedSkuVariantOnDisplay(currentVtoSku);
        this.addToCartFacade.changeEcomErrorState(1, true, "");
    }

    private getSkuObservableFromPageType(
        pageType: VtoPageType,
    ): Observable<string> {
        if (pageType === SINGLE_PRODUCT_PAGE) {
            return this.productPageFacade.currentProductReference$.pipe(
                filter(Boolean),
            );
        }
        return this.vtoBeautyFacade.localizedVtoCategories$.pipe(
            filter((result) => result.length > 0),
            take(1),
            map((localizedVtoCategories) =>
                getFirstSkuProductFromCategories(localizedVtoCategories),
            ),
        );
    }
}
